import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';

const AdminCompanies = () => {
  const { user: currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/companies');
      setCompanies(res.data.data || []);
    } catch {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  if (currentUser?.role !== 'admin') {
    return <div className="loading">Access denied</div>;
  }

  if (loading) return <div className="loading">Loading...</div>;

  const startEdit = (company) => {
    setEditingId(company.id);
    setEditName(company.name);
    setEditDomain(company.domain || '');
  };

  const saveEdit = async (company) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/companies/${company.id}`, { name: editName.trim(), domain: editDomain.trim() || null });
      setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, name: editName.trim(), domain: editDomain.trim() || null } : c));
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  const toggleStatus = async (company) => {
    const newStatus = company.status === 'active' ? 'inactive' : 'active';
    setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, status: newStatus } : c));
    try {
      await api.put(`/companies/${company.id}`, { status: newStatus });
    } catch (err) {
      toast.error('Failed to update status');
      fetchCompanies();
    }
  };

  const deleteCompany = async (company) => {
    if (company.user_count > 0) {
      toast.error('Cannot delete company with assigned users');
      return;
    }
    try {
      await api.delete(`/companies/${company.id}`);
      toast.success('Company deleted');
      setCompanies(prev => prev.filter(c => c.id !== company.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const addCompany = async () => {
    if (!newName.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await api.post('/companies', { name: newName.trim(), domain: newDomain.trim() || null });
      toast.success('Company created');
      setNewName('');
      setNewDomain('');
      setShowAdd(false);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Companies</h1>
        <div className="header-controls">
          <span className="user-count">{companies.length} companies</span>
          <button className="add-user-btn" onClick={() => setShowAdd(true)}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Company
          </button>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Domain</th>
              <th>Users</th>
              <th>Status</th>
              <th style={{ width: '150px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map(company => (
              <tr key={company.id}>
                <td>
                  {editingId === company.id ? (
                    <input
                      type="text"
                      className="inline-edit-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(company);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <strong>{company.name}</strong>
                  )}
                </td>
                <td>
                  {editingId === company.id ? (
                    <input
                      type="text"
                      className="inline-edit-input"
                      value={editDomain}
                      onChange={(e) => setEditDomain(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(company);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      placeholder="domain.com"
                    />
                  ) : (
                    company.domain || '--'
                  )}
                </td>
                <td>{company.user_count || 0}</td>
                <td>
                  <span
                    className={`status-dot ${company.status === 'active' ? 'active' : 'inactive'}`}
                    onClick={() => toggleStatus(company)}
                    style={{ cursor: 'pointer' }}
                  >
                    {company.status || 'active'}
                  </span>
                </td>
                <td>
                  {editingId === company.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button className="btn-save-sm" onClick={() => saveEdit(company)}>Save</button>
                      <button className="btn-cancel-sm" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button className="edit-user-btn" onClick={() => startEdit(company)} title="Edit">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      {(company.user_count || 0) === 0 && (
                        <button className="delete-user-btn" onClick={() => deleteCompany(company)} title="Delete">
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-content add-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Company</h2>
              <button className="modal-close" onClick={() => setShowAdd(false)}>&times;</button>
            </div>
            <div className="add-user-form">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Company name"
                  autoFocus
                />
              </div>
              <div className="form-field">
                <label>Domain</label>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="domain.com (optional)"
                />
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="btn-save" onClick={addCompany}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanies;
